import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { ApiError, isApiError } from '../../../api/errors';
import { Client, Ticket, TicketRequestState } from '../utils/commandCenterTypes';
import { TicketListFilters, listTicketsForFilters } from '../../../services/ticketService';

interface CommandCenterContextType {
  clients: Client[];
  setClients: (clients: Client[]) => void;
  currentClient: Client | null;
  setCurrentClient: (client: Client) => void;
  tickets: Ticket[];
  setTickets: (tickets: Ticket[]) => void;
  selectedTicket: Ticket | null;
  setSelectedTicket: (ticket: Ticket | null) => void;
  ticketRequestState: TicketRequestState;
  ticketRequestError: ApiError | null;
  fetchTickets: (filters?: TicketListFilters) => Promise<void>;
}

const CommandCenterContext = createContext<CommandCenterContextType | undefined>(undefined);

export const CommandCenterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketRequestState, setTicketRequestState] = useState<TicketRequestState>('idle');
  const [ticketRequestError, setTicketRequestError] = useState<ApiError | null>(null);

  const fetchTickets = useCallback(async (filters?: TicketListFilters) => {
    setTicketRequestState('loading');
    setTicketRequestError(null);

    try {
      const nextTickets = await listTicketsForFilters(filters);
      setTickets(nextTickets);
      setTicketRequestState(nextTickets.length > 0 ? 'ready' : 'empty');
    } catch (error) {
      if (isApiError(error)) {
        setTicketRequestError(error);

        if (error.kind === 'authorization') {
          setTicketRequestState('unauthorized');
        } else if (error.kind === 'forbidden') {
          setTicketRequestState('unauthorized');
        } else if (error.kind === 'unavailable') {
          setTicketRequestState('unavailable');
        } else {
          setTicketRequestState('error');
        }
      } else {
        setTicketRequestError({
          kind: 'unknown',
          message: 'Unable to load Command Centre tickets.',
        });
        setTicketRequestState('error');
      }

      setTickets([]);
    }
  }, []);

  return (
    <CommandCenterContext.Provider
      value={{
        clients,
        setClients,
        currentClient,
        setCurrentClient,
        tickets,
        setTickets,
        selectedTicket,
        setSelectedTicket,
        ticketRequestState,
        ticketRequestError,
        fetchTickets,
      }}
    >
      {children}
    </CommandCenterContext.Provider>
  );
};

export const useCommandCenter = () => {
  const context = useContext(CommandCenterContext);
  if (!context) throw new Error('useCommandCenter must be used within CommandCenterProvider');
  return context;
};
